import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { TiebreakMark } from './TiebreakMark';
import type { Placement } from '../../services/round-scores';

/** One line of a results sheet. */
export interface StandingsTableRow {
  id: string;
  name: string | null;
  team: string | null;
  /** The turn this athlete climbed in, on a class's table across its turns. */
  turn?: string | null;
  position: number;
  /** Climbed with an account of their own: a face beside the name. */
  hasAccount?: boolean;
  avatarUrl?: string | null;
  /** That account's profile slug: the name and the face link to it. */
  profileSlug?: string | null;
  /** What a tie-break did to the place: marked beside it. Left out on a stage's results. */
  tiebreak?: Placement['tiebreak'];
}

interface StandingsTableProps {
  rows: StandingsTableRow[];
  /** Whether to say which turn each row climbed in: a class split over turns. */
  showTurn?: boolean;
  /** The place as the first column, the way a results sheet reads. */
  positionFirst?: boolean;
}

/**
 * The sheet the room reads: who, for which squad, and where they came — and,
 * on a class ranked across its turns, which turn they climbed in.
 *
 * Shared by a closed round's standings (`RoundStandings`) and a class's across
 * its turns (`CategoryStandingsSheet`), so the two read the same. Somebody who
 * climbed with their own account has their avatar beside the name (initials
 * when they put none up); the others keep the space, so the names line up.
 * Both lead to their profile.
 */
export function StandingsTable({ rows, showTurn = false, positionFirst = false }: StandingsTableProps) {
  const { t } = useTranslation();
  const showAvatars = rows.some((row) => row.hasAccount);

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-muted-foreground">
          <tr>
            {positionFirst && (
              <th className="w-px px-3 py-2 text-left font-medium">{t('competitionRounds.standings.position')}</th>
            )}
            <th className="px-3 py-2 text-left font-medium">{t('competitionRounds.standings.athlete')}</th>
            <th className="px-3 py-2 text-left font-medium">{t('competitionRounds.standings.team')}</th>
            {showTurn && (
              <th className="px-3 py-2 text-left font-medium">{t('competitionRounds.standings.turn')}</th>
            )}
            {!positionFirst && (
              <th className="px-3 py-2 text-right font-medium">{t('competitionRounds.standings.position')}</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((row) => (
            <tr key={row.id}>
              {positionFirst && (
                <td className="px-3 py-2 font-semibold tabular-nums">
                  <Position row={row} />
                </td>
              )}
              <td className="px-3 py-2 font-medium">
                {showAvatars ? (
                  <ProfileLink slug={row.profileSlug} className="flex w-fit items-center gap-2">
                    {row.hasAccount ? (
                      <Avatar className="size-7" data-testid="standings-avatar">
                        {row.avatarUrl && <AvatarImage src={row.avatarUrl} alt="" />}
                        <AvatarFallback className="text-xs">{row.name?.charAt(0).toUpperCase() ?? '?'}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <span className="size-7 shrink-0" aria-hidden />
                    )}
                    {row.name ?? '—'}
                  </ProfileLink>
                ) : (
                  row.name ?? '—'
                )}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{row.team ?? '—'}</td>
              {showTurn && <td className="px-3 py-2 text-muted-foreground">{row.turn ?? '—'}</td>}
              {!positionFirst && (
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  <Position row={row} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The place, and what a tie-break did to it. */
function Position({ row }: { row: StandingsTableRow }) {
  if (!row.tiebreak) return <>{row.position}</>;

  return (
    <span className="inline-flex items-center gap-1.5">
      {row.position}
      <TiebreakMark tiebreak={row.tiebreak} />
    </span>
  );
}

/** The athlete's profile when they have one, plain content otherwise. */
function ProfileLink({ slug, className, children }: { slug?: string | null; className: string; children: ReactNode }) {
  if (!slug) return <span className={className}>{children}</span>;

  return (
    <Link to={`/users/${slug}`} className={`${className} hover:underline`}>
      {children}
    </Link>
  );
}

export default StandingsTable;
